
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Box, Stack, Typography } from "@mui/material";

import {
  HOPE_SANS,
  HOPE_SERIF,
} from "@/shared/branding/thehopeonebrand";

import type { ImpactCardData } from "../utils/impactCardData";
import type { AnchorCube } from "../utils/anchorCubes";
import { columnAnchorDomId } from "../utils/impactPages";
import {
  scrollImpactThankYouSubpage,
  useImpactThankYouSubpageSnap,
} from "@/cards/impact-card/hooks/useImpactThankYouSubpageSnap";

import { VerticalBookSheet } from "./ImpactCubePage";
import ImpactCardPresidentThankYouPage from "./ImpactCardPresidentThankYouPage";

const THANKS_AOL_COLUMN_ID = "thanks-aol-gratitude";
const PRESIDENT_LETTER_COLUMN_ID = "president-thank-you-letter";

type ImpactThankYouPageProps = {
  data: ImpactCardData;
  cube?: AnchorCube;
  isPartner?: boolean;
  isCreator?: boolean;
  mainCardId?: number;
  /** Only attach subpage snap while this book page owns the active scroller. */
  bookScrollActive?: boolean;
};

export default function ImpactThankYouPage({
  data: _data,
  cube: _cube,
  isPartner: _isPartner,
  isCreator: _isCreator,
  mainCardId,
  bookScrollActive = true,
}: ImpactThankYouPageProps) {
  useImpactThankYouSubpageSnap(
    THANKS_AOL_COLUMN_ID,
    PRESIDENT_LETTER_COLUMN_ID,
    bookScrollActive
  );

  const goToPresidentLetter = () => {
    scrollImpactThankYouSubpage(1);
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#FAF8F2",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      {/* SUBPAGE 1 — anchor cube sheet (Art of Living Nepal) */}
      <VerticalBookSheet
        fit="page"
        snapStop="always"
        id={columnAnchorDomId(THANKS_AOL_COLUMN_ID)}
      >
        <Stack
          spacing={{ xs: 1.5, sm: 1.85 }}
          sx={{
            flex: 1,
            minHeight: 0,
            px: { xs: 2.25, sm: 3 },
            pt: { xs: 1.75, sm: 3 },
            pb: { xs: 1, sm: 1.5 },
            justifyContent: "flex-start",
          }}
        >
          <Stack direction="row" spacing={0.8} alignItems="center">
            <FavoriteRoundedIcon
              sx={{ fontSize: { xs: 18, sm: 16 }, color: "#C99B4D" }}
            />
            <Typography
              sx={{
                fontFamily: HOPE_SANS,
                fontSize: { xs: "0.72rem", sm: "0.65rem" },
                fontWeight: 800,
                letterSpacing: "0.1em",
                color: "#5E8F72",
              }}
            >
              감사의 마음
            </Typography>
          </Stack>

          <Box>
            <Typography
              component="h1"
              sx={{
                fontFamily: HOPE_SERIF,
                fontSize: { xs: "1.72rem", sm: "1.9rem" },
                fontWeight: 700,
                lineHeight: 1.32,
                letterSpacing: "-0.035em",
                color: "#102C24",
                wordBreak: "keep-all",
              }}
            >
              진심을 담아
              <br />
              감사드립니다.
            </Typography>
            <Box
              sx={{
                mt: 1.1,
                width: 34,
                height: 2,
                borderRadius: 999,
                bgcolor: "#C99B4D",
              }}
            />
          </Box>

          <Typography sx={messageSx}>
            Art of Living Nepal을 대표하여 네팔 구호 활동을 위해 소중한 기금을
            보내주신 데 깊이 감사드립니다.
          </Typography>
          <Typography sx={messageSx}>
            보내주신 신뢰와 정성을 소중히 여기며, 지원금이 구호 활동에 의미
            있게 사용되고 피해 주민들이 안전한 일상을 되찾을 수 있도록 최선을
            다하겠습니다.
          </Typography>
          <Typography sx={messageSx}>
            네팔의 이웃들을 위해 함께해 주신 모든 분께 다시 한번 진심으로
            감사드립니다.
          </Typography>

          <Box sx={{ pt: 0.35 }}>
            <Typography
              sx={{
                fontFamily: HOPE_SANS,
                fontSize: { xs: "0.82rem", sm: "0.78rem" },
                color: "#5A6B63",
                mb: 0.35,
              }}
            >
              감사의 마음을 담아,
            </Typography>
            <Typography
              sx={{
                fontFamily: HOPE_SERIF,
                fontWeight: 700,
                fontSize: { xs: "1.12rem", sm: "1rem" },
                color: "#1D4437",
              }}
            >
              Art of Living Nepal
            </Typography>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={goToPresidentLetter}
            sx={{
              mt: "auto",
              pt: 1,
              pb: 0.5,
              width: "100%",
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              textAlign: "center",
              font: "inherit",
              color: "inherit",
            }}
          >
            
            <KeyboardArrowDownRoundedIcon
              sx={{ mt: 0.15, fontSize: 26, color: "#5E8F72" }}
            />
          </Box>
        </Stack>
      </VerticalBookSheet>

      {/* SUBPAGE 2 — president letter (TOC column anchor) */}
      <VerticalBookSheet
        fit="page"
        snapStop="always"
        id={columnAnchorDomId(PRESIDENT_LETTER_COLUMN_ID)}
      >
        <ImpactCardPresidentThankYouPage
          variant="subpage"
          mainCardId={mainCardId}
        />
      </VerticalBookSheet>
    </Box>
  );
}

const messageSx = {
  fontFamily: HOPE_SANS,
  fontSize: { xs: "0.98rem", sm: "0.98rem" },
  lineHeight: { xs: 1.72, sm: 1.8 },
  color: "#4A5B52",
  wordBreak: "keep-all" as const,
  textAlign: "left" as const,
};
